<template>
  <form :action="formAction" method="post" @submit="submitting = true">
    <input v-if="csrfToken" type="hidden" name="authenticity_token" :value="csrfToken">

    <div v-if="errors.length" class="alert alert-danger">
      <strong>Unable to start clone.</strong>
      <ul class="mb-0"><li v-for="message in errors" :key="message">{{ message }}</li></ul>
    </div>

    <table class="hv-form-table">
      <tbody id="vm-nic-fields">
      <tr>
        <th>Hostname</th>
        <td>
          <input v-model="hostname" name="cloud_init[hostname]" type="text" class="form-control" maxlength="63" required>
          <small class="text-muted">Used as both the SCC VM name and the hostname inside the guest.</small>
        </td>
      </tr>
      <tr>
        <th>Initial User</th>
        <td>
          <input v-model="username" name="cloud_init[username]" type="text" class="form-control" maxlength="32" required>
          <small class="text-muted">This user receives passwordless sudo access.</small>
        </td>
      </tr>
      <tr>
        <th>SSH Public Keys</th>
        <td>
          <textarea v-model="sshKeys" name="cloud_init[ssh_authorized_keys]" class="form-control" rows="5" placeholder="ssh-ed25519 AAAA..." required></textarea>
          <small class="text-muted">Enter one public key per line. Password and root SSH login remain disabled.</small>
        </td>
      </tr>

      <tr>
        <th>NICs</th>
        <td>
          <div class="vm-nic-controls">
            <span class="badge badge-secondary">{{ interfaces.length }}</span>
            <button type="button" class="btn btn-sm btn-outline-primary" :disabled="interfaces.length >= 8" @click="addInterface">Add</button>
          </div>
        </td>
      </tr>

      <template v-for="(network, index) in interfaces" :key="`cloud-init-nic-${index}`">
        <tr class="vm-nic-group-heading">
          <td colspan="2">
            <div class="vm-nic-group-bar">
              <span class="vm-nic-chip">NIC {{ index + 1 }}</span>
              <button v-if="interfaces.length > 1" type="button" class="vm-nic-inline-remove" @click="removeInterface(index)">Remove</button>
            </div>
          </td>
        </tr>
        <tr class="vm-nic-row vm-nic-row-start">
          <th>Network Type</th>
          <td>
            <input type="hidden" name="cloud_init[network_interfaces][][if_id]" :value="index + 1">
            <VmNicSelect
              :options="networkTypeOptions"
              :initial-value="network.network_type"
              input-name="cloud_init[network_interfaces][][network_type]"
              empty-label="Select a network type"
              filter-placeholder="Filter network types"
              @update:value="updateNetworkType(network, $event)"
            />
          </td>
        </tr>
        <tr class="vm-nic-row">
          <th>{{ network.network_type === 'vxlan' ? 'VXLAN Segment' : 'Network' }}</th>
          <td v-if="network.network_type === 'vxlan'">
            <VmNicSelect
              :key="`vxlan-picker-${index}`"
              :options="vxlanOptions"
              :initial-value="network.segment_id"
              input-name="cloud_init[network_interfaces][][segment_id]"
              empty-label="Select a VXLAN segment"
              filter-placeholder="Filter VXLAN segments"
              @update:value="network.segment_id = $event"
            />
          </td>
          <td v-else>
            <VmNicSelect
              :key="`network-picker-${index}`"
              :options="networkOptions"
              :initial-value="network.network_id"
              input-name="cloud_init[network_interfaces][][network_id]"
              empty-label="Select a network"
              filter-placeholder="Filter networks"
              @update:value="network.network_id = $event"
            />
          </td>
        </tr>
        <tr class="vm-nic-row">
          <th>Address Mode</th>
          <td>
            <VmNicSelect
              :options="ipv4ModeOptions"
              :initial-value="network.mode"
              input-name="cloud_init[network_interfaces][][mode]"
              empty-label="Select IPv4 mode"
              filter-placeholder="Filter IPv4 modes"
              @update:value="network.mode = $event"
            />
          </td>
        </tr>
        <template v-if="network.mode === 'static'">
          <tr class="vm-nic-row">
            <th>IPv4 Address</th>
            <td><input v-model="network.ipv4_address" name="cloud_init[network_interfaces][][ipv4_address]" type="text" class="form-control" placeholder="192.0.2.10/24" required></td>
          </tr>
          <tr class="vm-nic-row">
            <th>Default Gateway</th>
            <td><input v-model="network.ipv4_gateway" name="cloud_init[network_interfaces][][ipv4_gateway]" type="text" class="form-control" placeholder="192.0.2.1"></td>
          </tr>
          <tr class="vm-nic-row">
            <th>DNS Servers</th>
            <td>
              <input v-model="network.dns_servers" name="cloud_init[network_interfaces][][dns_servers]" type="text" class="form-control" placeholder="192.0.2.53, 192.0.2.54">
              <small class="text-muted">Separate multiple addresses with commas.</small>
            </td>
          </tr>
        </template>
        <tr v-else class="vm-nic-row">
          <th>Configuration</th>
          <td class="text-muted">IPv4 address, gateway, and DNS will be supplied by DHCP.</td>
        </tr>
        <tr class="vm-nic-row">
          <th>IPv6 Mode</th>
          <td>
            <VmNicSelect
              :options="ipv6ModeOptions"
              :initial-value="network.ipv6_mode"
              input-name="cloud_init[network_interfaces][][ipv6_mode]"
              empty-label="Select IPv6 mode"
              filter-placeholder="Filter IPv6 modes"
              @update:value="network.ipv6_mode = $event"
            />
          </td>
        </tr>
        <template v-if="network.ipv6_mode === 'static'">
          <tr class="vm-nic-row">
            <th>IPv6 Address</th>
            <td>
              <input v-model="network.ipv6_address" name="cloud_init[network_interfaces][][ipv6_address]" type="text" class="form-control" placeholder="2001:db8::10/64" required>
              <small class="text-muted">CIDR prefix is required.</small>
            </td>
          </tr>
          <tr class="vm-nic-row vm-nic-row-end">
            <th>IPv6 Gateway</th>
            <td><input v-model="network.ipv6_gateway" name="cloud_init[network_interfaces][][ipv6_gateway]" type="text" class="form-control" placeholder="2001:db8::1"></td>
          </tr>
        </template>
        <tr v-else-if="network.ipv6_mode === 'auto'" class="vm-nic-row vm-nic-row-end">
          <th>IPv6 Configuration</th>
          <td class="text-muted">IPv6 configuration will use router advertisements and DHCPv6.</td>
        </tr>
        <tr v-else class="vm-nic-row vm-nic-row-end">
          <th>IPv6 Configuration</th>
          <td class="text-muted">IPv6 autoconfiguration is disabled.</td>
        </tr>
      </template>
      </tbody>
    </table>

    <div class="vm-form-actions">
      <button type="submit" class="btn btn-primary" :disabled="submitting">Clone and Start</button>
      <a :href="backUrl" class="btn btn-default">Back</a>
    </div>
  </form>
</template>

<script setup>
import { ref } from "vue"
import VmNicSelect from "./VmNicSelect.vue"

const props = defineProps({
  formAction: { type: String, required: true },
  backUrl: { type: String, required: true },
  initialHostname: { type: String, default: "" },
  initialUsername: { type: String, default: "cloud-user" },
  initialSshKeys: { type: String, default: "" },
  initialInterfaces: { type: Array, default: () => [] },
  interfaceCount: { type: Number, default: 1 },
  networkOptions: { type: Array, default: () => [] },
  vxlanOptions: { type: Array, default: () => [] },
  errors: { type: Array, default: () => [] }
})

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
const hostname = ref(props.initialHostname)
const username = ref(props.initialUsername)
const sshKeys = ref(props.initialSshKeys)
const submitting = ref(false)
const networkTypeOptions = [
  { label: "VLAN", value: "vlan" },
  { label: "VXLAN", value: "vxlan" }
]
const ipv4ModeOptions = [
  { label: "DHCP", value: "dhcp" },
  { label: "Static IPv4", value: "static" }
]
const ipv6ModeOptions = [
  { label: "Disabled", value: "disabled" },
  { label: "SLAAC / DHCPv6", value: "auto" },
  { label: "Static IPv6", value: "static" }
]
const interfaces = ref(Array.from({ length: Math.max(props.interfaceCount, 1) }, (_, index) => ({
  mode: props.initialInterfaces?.[index]?.mode || "dhcp",
  ipv6_mode: props.initialInterfaces?.[index]?.ipv6_mode || "auto",
  network_type: props.initialInterfaces?.[index]?.network_type || "vlan",
  network_id: String(props.initialInterfaces?.[index]?.network_id || props.networkOptions?.[0]?.value || ""),
  segment_id: String(props.initialInterfaces?.[index]?.segment_id || props.vxlanOptions?.[0]?.value || ""),
  ipv4_address: props.initialInterfaces?.[index]?.ipv4_address || "",
  ipv4_gateway: props.initialInterfaces?.[index]?.ipv4_gateway || "",
  ipv6_address: props.initialInterfaces?.[index]?.ipv6_address || "",
  ipv6_gateway: props.initialInterfaces?.[index]?.ipv6_gateway || "",
  dns_servers: props.initialInterfaces?.[index]?.dns_servers || ""
})))

function addInterface() {
  if (interfaces.value.length >= 8) return
  interfaces.value.push({
    mode: "dhcp",
    ipv6_mode: "auto",
    network_type: "vlan",
    network_id: String(props.networkOptions?.[0]?.value || ""),
    segment_id: String(props.vxlanOptions?.[0]?.value || ""),
    ipv4_address: "",
    ipv4_gateway: "",
    ipv6_address: "",
    ipv6_gateway: "",
    dns_servers: ""
  })
}

function removeInterface(index) {
  if (interfaces.value.length <= 1) return
  interfaces.value.splice(index, 1)
}

function ensureNetworkSelection(network) {
  if (network.network_type === "vlan" && !network.network_id) {
    network.network_id = String(props.networkOptions?.[0]?.value || "")
  }
  if (network.network_type === "vxlan" && !network.segment_id) {
    network.segment_id = String(props.vxlanOptions?.[0]?.value || "")
  }
}

function updateNetworkType(network, value) {
  network.network_type = value
  ensureNetworkSelection(network)
}
</script>
